import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle2, Compass } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('polling');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/precios');
      return;
    }
    pollStatus(0);
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/api/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error();

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('paid');

        // Google Ads conversion event
        if (typeof window.gtag === 'function') {
          const conversionValue = data.plan_id === 'pro_annual' ? 990 : 129;
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18303994422/7a81CM36tc4cELaUg5hE',
            'value': conversionValue,
            'currency': 'MXN',
            'transaction_id': data.payment_intent || sessionId,
          });
        }

        // Refresh user data to get updated plan
        const meResponse = await authFetch(`${API_URL}/api/auth/me`);
        if (meResponse.ok) {
          const userData = await meResponse.json();
          updateUser(userData);
        }
        setTimeout(() => navigate('/dashboard'), 2500);
        return;
      }

      if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollStatus(attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Compass className="w-8 h-8 text-[#2563EB]" />
          <span className="font-bold text-2xl tracking-tight font-['Manrope']">Klarvi</span>
        </div>
        <Card className="border shadow-lg">
          <CardContent className="text-center py-12 space-y-4">
            {status === 'polling' && (
              <>
                <Loader2 className="w-12 h-12 animate-spin text-[#2563EB] mx-auto" />
                <h2 className="text-xl font-semibold font-['Manrope']">Verificando tu pago...</h2>
                <p className="text-muted-foreground">Esto solo toma unos segundos.</p>
              </>
            )}
            {status === 'paid' && (
              <>
                <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto" />
                <h2 className="text-xl font-semibold font-['Manrope']" data-testid="payment-success-msg">
                  ¡Bienvenido a Pro! Ya tienes acceso completo.
                </h2>
                <p className="text-muted-foreground">Redirigiendo al dashboard...</p>
              </>
            )}
            {(status === 'failed' || status === 'timeout') && (
              <>
                <h2 className="text-xl font-semibold font-['Manrope']">Hubo un problema</h2>
                <p className="text-muted-foreground">No pudimos verificar tu pago. Contacta soporte si el cobro fue realizado.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
